import { PDFDocument, StandardFonts, degrees, rgb } from 'pdf-lib';
import { imagePoint, layoutPins, type PdfPin } from './drawing-layout.js';

export function pdfPoint(x:number,y:number,box:{x:number;y:number;width:number;height:number},rotation:number):{x:number;y:number} {
  const r=((rotation%360)+360)%360;
  if(r===90)return {x:box.x+y,y:box.y+x};
  if(r===180)return {x:box.x+box.width-x,y:box.y+y};
  if(r===270)return {x:box.x+box.width-y,y:box.y+box.height-x};
  return {x:box.x+x,y:box.y+box.height-y};
}

// Add vector pins to a copy of every PDF page, preserving the source drawing.
export async function drawingPdfWithPins(bytes:Uint8Array,pins:PdfPin[]):Promise<Uint8Array> {
  const document=await PDFDocument.load(bytes,{updateMetadata:false});
  const font=await document.embedFont(StandardFonts.HelveticaBold);
  const pages=document.getPages(), colour=rgb(.45,.12,.06);
  for(const pin of pins)if(!Number.isInteger(pin.page) || pin.page<1 || pin.page>pages.length)throw new Error(`Snag ${pin.number} refers to a missing PDF page. Edit its pin before exporting.`);
  for(const [index,page] of pages.entries()) {
    const box=page.getCropBox(), rotation=((page.getRotation().angle%360)+360)%360;
    const width=rotation%180 ? box.height : box.width, height=rotation%180 ? box.width : box.height;
    const pagePins=pins.filter(pin=>pin.page===index+1).map(pin=>{
      if(pin.space==='image')return pin;
      const point=imagePoint(pin.x*297,pin.y*210,297,210,width/height);
      if(!point)throw new Error(`Snag ${pin.number} is outside its drawing. Edit its pin before exporting.`);
      return {...pin,...point,space:'image' as const};
    });
    const scale=width/800;
    const positions=layoutPins(pagePins,800,height/scale,width/height);
    for(const pin of positions) {
      const anchor=pdfPoint(pin.anchorX*scale,pin.anchorY*scale,box,rotation),label=pdfPoint(pin.labelX*scale,pin.labelY*scale,box,rotation);
      page.drawLine({start:anchor,end:label,thickness:.8*scale,color:colour});
      page.drawCircle({...anchor,size:3.5*scale,borderColor:colour,borderWidth:1*scale,color:rgb(1,1,1)});
      page.drawCircle({...anchor,size:1.3*scale,color:colour});
      const labelWidth=(font.widthOfTextAtSize(pin.number,12)+8)*scale;
      const background=pdfPoint(pin.labelX*scale-labelWidth/2,(pin.labelY+9)*scale,box,rotation);
      page.drawRectangle({...background,width:labelWidth,height:20*scale,color:rgb(1,1,1),opacity:.9,rotate:degrees(rotation)});
      const baseline=pdfPoint(pin.labelX*scale-font.widthOfTextAtSize(pin.number,12)*scale/2,(pin.labelY+4)*scale,box,rotation);
      page.drawText(pin.number,{...baseline,size:12*scale,font,color:colour,rotate:degrees(rotation)});
    }
  }
  return document.save();
}
